import {
	ApartmentOutlined,
	BankOutlined,
	BellOutlined,
	CalendarOutlined,
	CarOutlined,
	CheckSquareOutlined,
	CloseOutlined,
	DashboardOutlined,
	EnvironmentOutlined,
	ExperimentOutlined,
	GlobalOutlined,
	IdcardOutlined,
	LogoutOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	ScanOutlined,
	SettingOutlined,
	UnorderedListOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Button, Dropdown, Layout, Menu, Typography } from "antd";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { colors } from "@/app/theme/tokens";
import { useLogout, useMe } from "@/modules/auth/hooks/useAuth";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { ORGANIZATION_QUERY_KEY } from "@/modules/organizations/hooks/useOrganizations";
import { organizationService } from "@/modules/organizations/services/organization.service";
import { SidebarLiveMap } from "./SidebarLiveMap";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuItems = [
	{
		key: "/dashboard",
		icon: <DashboardOutlined />,
		label: "Boshqaruv paneli",
	},
	{
		key: "/equipments",
		icon: <EnvironmentOutlined />,
		label: "GPS Xarita",
	},
	{
		key: "/monitoring",
		icon: <GlobalOutlined />,
		label: "Monitoring",
	},
	{
		key: "/notifications",
		icon: <BellOutlined />,
		label: "Bildirishnomalar",
	},
	{
		key: "davomat",
		icon: <CalendarOutlined />,
		label: "Davomat",
		children: [
			{
				key: "/attendance/daily",
				label: "Davomat hisobotlari",
			},
			{
				key: "/attendance/employees",
				label: "Davomat qo'shish",
			},
		],
	},
	{
		key: "ishlar",
		icon: <CheckSquareOutlined />,
		label: "Ishlar",
		children: [
			{
				key: "/tasks",
				icon: <UnorderedListOutlined />,
				label: "Topshiriqlar",
			},
			{
				key: "/task-types",
				icon: <CheckSquareOutlined />,
				label: "Topshiriq turlari",
			},
			{
				key: "/task-schedule",
				icon: <CalendarOutlined />,
				label: "Takrorlanish",
			},
		],
	},
	{
		key: "tashkilotlar",
		icon: <BankOutlined />,
		label: "Tashkilotlar",
		children: [
			{
				key: "/organizations",
				label: "Quyi tashkilotlar",
			},
			{
				key: "/cross-access",
				label: "Boshqa tashkilotlar",
			},
		],
	},
	{
		key: "ekinlar",
		icon: <ExperimentOutlined />,
		label: "Ekinlar",
		children: [
			{
				key: "/plantings",
				label: "Ekinzorlar xaritasi",
			},
			{
				key: "/plants",
				label: "Ekin turlari",
			},
			{
				key: "/plant-contracts",
				label: "Ekin shartnomalari",
			},
		],
	},
	{
		key: "sozlamalar",
		icon: <SettingOutlined />,
		label: "Sozlamalar",
		children: [
			{
				key: "/settings/profile",
				icon: <UserOutlined />,
				label: "Profil",
			},
			{
				key: "/users",
				icon: <UserOutlined />,
				label: "Foydalanuvchilar",
			},
			{
				key: "/organization-types",
				icon: <ApartmentOutlined />,
				label: "Tashkilot turlari",
			},
			{
				key: "/positions",
				icon: <IdcardOutlined />,
				label: "Lavozimlar",
			},
			{
				key: "/settings/faceid",
				icon: <ScanOutlined />,
				label: "FaceID qurilmalari",
			},
			{
				key: "/settings/equipment-types",
				icon: <CarOutlined />,
				label: "Texnika turlari",
			},
		],
	},
];

const CHILDREN_PREFETCH_LIMIT = 10;

export function MainLayout() {
	const [collapsed, setCollapsed] = useState(false);
	const [mapPanelOpen, setMapPanelOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { data: meUser } = useMe();
	const storeUser = useAuthStore((state) => state.user);
	const user = meUser ?? storeUser;
	const { mutate: logout, isPending: isLoggingOut } = useLogout();

	const parentOrgId =
		meUser?.organizations?.find((o) => o.isPrimary)?.id ?? meUser?.organizations?.[0]?.id;

	useEffect(() => {
		if (!parentOrgId || location.pathname === "/organizations") {
			return;
		}
		queryClient.prefetchQuery({
			queryKey: [
				ORGANIZATION_QUERY_KEY,
				"children",
				parentOrgId,
				{ page: 1, limit: CHILDREN_PREFETCH_LIMIT },
			],
			queryFn: () =>
				organizationService.listChildren(parentOrgId, { page: 1, limit: CHILDREN_PREFETCH_LIMIT }),
		});
	}, [parentOrgId, location.pathname, queryClient]);

	const userMenuItems = [
		{
			key: "profile",
			icon: <UserOutlined />,
			label: "Profil",
		},
		{
			type: "divider" as const,
		},
		{
			key: "logout",
			icon: <LogoutOutlined />,
			label: "Chiqish",
			danger: true,
		},
	];

	const handleUserMenuClick = ({ key }: { key: string }) => {
		if (key === "logout") {
			logout();
		} else if (key === "profile") {
			navigate("/settings/profile");
		}
	};

	const handleMenuClick = ({ key }: { key: string }) => {
		navigate(key);
	};

	const getOpenKeys = (): string[] => {
		const path = location.pathname;
		// First matching prefix wins; order matters for overlapping prefixes.
		const openKeyByPrefix: Array<{ prefixes: string[]; key: string }> = [
			{ prefixes: ["/tasks", "/task-types", "/task-schedule"], key: "ishlar" },
			{ prefixes: ["/attendance"], key: "davomat" },
			{ prefixes: ["/payments"], key: "tolovlar" },
			{ prefixes: ["/debts"], key: "qarzdorliklar" },
			{ prefixes: ["/organizations", "/cross-access"], key: "tashkilotlar" },
			{ prefixes: ["/plants", "/plant-contracts", "/plantings"], key: "ekinlar" },
			{
				prefixes: [
					"/users",
					"/organization-types",
					"/positions",
					"/settings/staff",
					"/settings/task-types",
					"/settings/equipment-types",
					"/gps-devices",
					"/settings",
				],
				key: "sozlamalar",
			},
		];
		const match = openKeyByPrefix.find((entry) =>
			entry.prefixes.some((prefix) => path.startsWith(prefix))
		);
		return match ? [match.key] : [];
	};

	return (
		<Layout className="min-h-screen">
			<Sider
				trigger={null}
				collapsible
				collapsed={collapsed}
				theme="dark"
				width={280}
				style={{ background: colors.sider.bg }}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						height: "100%",
						overflow: "hidden",
					}}
				>
					<div className="h-23 flex items-center justify-center" style={{ flexShrink: 0 }}>
						<Text strong className="text-white text-xl">
							{collapsed ? (
								<img src="/logoM.png" alt="LogoM" className="h-9 w-9" />
							) : (
								<img src="/logo.png" alt="Logo" className="" />
							)}
						</Text>
					</div>

					<div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
						<Menu
							theme="dark"
							mode="inline"
							selectedKeys={[location.pathname]}
							defaultOpenKeys={getOpenKeys()}
							items={menuItems}
							onClick={handleMenuClick}
							style={{ background: colors.sider.bg, fontSize: 15 }}
						/>
					</div>
				</div>
			</Sider>

			<Layout>
				<Header className="bg-white px-4 flex items-center justify-between border-b border-neutral-200">
					<Button
						type="text"
						icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
						onClick={() => setCollapsed(!collapsed)}
						className="text-lg"
					/>
					<div className="flex items-center gap-2">
						<Dropdown
							menu={{
								items: userMenuItems,
								onClick: handleUserMenuClick,
							}}
							placement="bottomRight"
							trigger={["click"]}
							disabled={isLoggingOut}
						>
							<div className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-3 py-1 rounded">
								<Avatar icon={<UserOutlined />} />
								<Text className="hidden sm:inline">{user?.phone}</Text>
							</div>
						</Dropdown>
					</div>
				</Header>

				<Layout style={{ position: "relative" }}>
					<Content className="m-4 p-6 bg-white rounded-lg min-h-70">
						<Outlet />
					</Content>

					{mapPanelOpen && (
						<Sider
							width={390}
							theme="light"
							style={{
								background: "#fff",
								borderLeft: "1px solid #e5e7eb",
								display: "flex",
								flexDirection: "column",
								overflow: "hidden",
							}}
						>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
								}}
							>
								<div
									style={{
										padding: "12px 16px",
										borderBottom: "1px solid #f0f0f0",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										flexShrink: 0,
									}}
								>
									<div>
										<span style={{ fontWeight: 700, fontSize: 15 }}>GPS Xarita</span>
										<span
											style={{
												marginLeft: 8,
												fontSize: 12,
												color: "#9ca3af",
												background: "#f3f4f6",
												borderRadius: 4,
												padding: "2px 6px",
											}}
										>
											Live
										</span>
									</div>
									<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
										<Button
											size="small"
											type="link"
											onClick={() => navigate("/equipments")}
											style={{ padding: 0 }}
										>
											To&apos;liq ko&apos;rish →
										</Button>
										<Button
											size="small"
											type="text"
											icon={<CloseOutlined />}
											onClick={() => setMapPanelOpen(false)}
										/>
									</div>
								</div>

								<div style={{ flex: 1, minHeight: 0 }}>
									<SidebarLiveMap />
								</div>
							</div>
						</Sider>
					)}
				</Layout>
			</Layout>
		</Layout>
	);
}
